import { useMutation, useQuery } from '@apollo/client';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import moment from 'moment';
import 'moment/locale/fr'; // 🇫🇷 importer la locale française

moment.locale('fr'); // ✅ activer le français

import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import {
  deleteConsumers,
  getConsumers,
  updateConsumer,
} from '../../../api/apollo';
import DeleteConfirmationDialog from '../../molecules/DeleteConfirmationDialog';

interface Consumer {
  consumerID: string;
  fullName: string;
  phone: string;
  createdAt: string;
}

type Order = 'asc' | 'desc';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: string | number },
  b: { [key in Key]: string | number },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

interface HeadCell {
  id: keyof Consumer | 'actions';
  label: string;
  numeric: boolean;
  disablePadding: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'consumerID',
    numeric: false,
    disablePadding: true,
    label: 'Identificateur',
  },
  {
    id: 'fullName',
    numeric: false,
    disablePadding: false,
    label: 'Nom complet',
  },
  {
    id: 'phone',
    numeric: false,
    disablePadding: false,
    label: 'Téléphone',
  },
  {
    id: 'createdAt',
    numeric: false,
    disablePadding: false,
    label: 'Date de Création',
  },
  {
    id: 'actions',
    numeric: false,
    disablePadding: false,
    label: 'Actions',
  },
];

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Consumer,
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler =
    (property: keyof Consumer) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ 'aria-label': 'select all consumers' }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ fontWeight: 'bold' }}
          >
            {headCell.id !== 'actions' ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface EnhancedTableToolbarProps {
  numSelected: number;
  handleDeleteConfirm: () => void;
}

const EnhancedTableToolbar = ({
  numSelected,
  handleDeleteConfirm,
}: EnhancedTableToolbarProps) => {
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity,
            ),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} sélectionné
        </Typography>
      ) : (
        <Typography
          sx={{ flex: '1 1 100%', fontWeight: 'bold' }}
          variant="h6"
          component="div"
        >
          Liste des consommateurs
        </Typography>
      )}
      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton onClick={handleDeleteConfirm}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filtre">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
};

export default function ConsumerList() {
  const { loading, error, data, refetch } = useQuery(getConsumers);
  const [consumers, setConsumers] = useState<Consumer[]>([]);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Consumer>('fullName');
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedConsumer, setSelectedConsumer] = useState<Consumer | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<string[]>([]);
  console.log('==>consumers', consumers);
  useEffect(() => {
    if (data?.consumers) {
      setConsumers(data.consumers);
    }
  }, [data]);

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof Consumer,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = consumers.map((n) => n.consumerID);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (_event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - consumers.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...consumers]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, consumers],
  );

  const [deleteConsumer] = useMutation(deleteConsumers, {
    onCompleted: () => {
      enqueueSnackbar('Consommateurs supprimés avec succès', {
        variant: 'success',
      });
      refetch();
      setDeleteTarget([]);
      setSelected([]);
    },
    onError: () => {
      enqueueSnackbar('Erreur lors de la suppression des consommateurs', {
        variant: 'error',
      });
    },
  });

  const handleDeleteConfirm = () => {
    setDeleteTarget(selected);
  };

  const handleDelete = async () => {
    if (deleteTarget.length === 0) return;

    try {
      await deleteConsumer({
        variables: { consumerIDs: deleteTarget },
      });
    } catch (error) {
      console.error('Error deleting consumers:', error);
    }
  };

  const [updateConsumerMutation] = useMutation(updateConsumer, {
    onCompleted: () => {
      enqueueSnackbar('Consommateur mis à jour avec succès', {
        variant: 'success',
      });
      refetch();
      setOpenEditModal(false);
    },
    onError: () => {
      enqueueSnackbar('Erreur lors de la mise à jour du consommateur', {
        variant: 'error',
      });
    },
  });

  const handleUpdateConsumer = () => {
    if (!selectedConsumer) return;
    updateConsumerMutation({
      variables: {
        consumerID: selectedConsumer.consumerID,
        fullName: selectedConsumer.fullName,
        phone: selectedConsumer.phone,
      },
    });
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (error)
    return <Typography color="error">Error: {error.message}</Typography>;

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <EnhancedTableToolbar
          numSelected={selected.length}
          handleDeleteConfirm={handleDeleteConfirm}
        />
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={consumers.length}
            />
            <TableBody>
              {visibleRows.map((row) => {
                const isItemSelected = selected.includes(row.consumerID);
                const labelId = `enhanced-table-checkbox-${row.consumerID}`;

                return (
                  <TableRow
                    hover
                    onClick={(event) => handleClick(event, row.consumerID)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.consumerID}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                    >
                      {row.consumerID}
                    </TableCell>
                    <TableCell align="left">{row.fullName}</TableCell>
                    <TableCell align="left">{row.phone}</TableCell>
                    <TableCell align="left">
                      {moment(row.createdAt).format('DD MMM YYYY')}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setSelectedConsumer(row); // Set the selected consumer
                          setOpenEditModal(true); // Open the edit dialog
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={consumers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page"
        />
      </Paper>
      <FormControlLabel
        control={<Switch checked={dense} onChange={handleChangeDense} />}
        label="Marge dense"
      />
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)}>
        <DialogTitle>Modifier le consommateur</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Nom Complet"
            value={selectedConsumer?.fullName || ''}
            onChange={(e) =>
              setSelectedConsumer((prev) =>
                prev ? { ...prev, fullName: e.target.value } : null,
              )
            }
          />
          <TextField
            fullWidth
            margin="dense"
            label="Téléphone"
            value={selectedConsumer?.phone || ''}
            onChange={(e) =>
              setSelectedConsumer((prev) =>
                prev ? { ...prev, phone: e.target.value } : null,
              )
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditModal(false)} color="secondary">
            Annuler
          </Button>
          <Button onClick={handleUpdateConsumer} color="primary">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
      <DeleteConfirmationDialog
        open={deleteTarget.length > 0}
        onClose={() => setDeleteTarget([])}
        onConfirm={handleDelete}
        message="Êtes-vous sûr de vouloir supprimer le(s) consommateur(s) ? Cette action est irréversible.

        NB : Toutes les informations relatives seront supprimées, y compris les Compteurs, Factures, Paiements et Dettes."
      />
    </Box>
  );
}
